import { useRef } from 'react';

import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';

import { isDefined, isNotDefined } from '@enterprise/core';
import { DefaultToolTip, FloppyDisk, Gear, TargetedLoader } from '@enterprise/ui';

import type { SettingsModalProps } from './types/settings-modal.props';

export default function SettingsModal({
  buttonElement,
  children,
  formRef,
  header,
  includeCloseButton = true,
  initialFocusRef,
  isLoading = false,
  isOpen,
  isSaveDisabled = false,
  onCancel,
  onClose,
  onOpen,
  onSave,
  showDefaultButtons = true,
}: SettingsModalProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <>
      {isDefined(buttonElement) && buttonElement}
      {isNotDefined(buttonElement) && showDefaultButtons && (
        <DefaultToolTip label="View settings">
          <Box
            _hover={{ color: 'gray.900' }}
            aria-label="View settings"
            className="transition-colors"
            color="gray.400"
            cursor="pointer"
            onClick={onOpen}>
            <Gear
              size="xl"
              widthAuto
            />
          </Box>
        </DefaultToolTip>
      )}
      <Modal
        blockScrollOnMount
        initialFocusRef={initialFocusRef}
        isOpen={isOpen}
        motionPreset="slideInBottom"
        onClose={onClose}>
        <ModalOverlay />
        <ModalContent maxW="50rem">
          <ModalHeader fontSize="x-large">{header}</ModalHeader>
          {includeCloseButton && <ModalCloseButton />}
          <ModalBody
            position="relative"
            ref={ref}>
            <TargetedLoader
              hideChildren={false}
              loading={isLoading}
              ref={ref}
              size={2}
              type="dual-ring">
              {children}
            </TargetedLoader>
          </ModalBody>
          <ModalFooter
            gap={3}
            justifyContent="end">
            <Button
              onClick={onCancel}
              type="button">
              Cancel
            </Button>
            <Button
              colorScheme="hsp"
              isDisabled={isSaveDisabled}
              leftIcon={<FloppyDisk />}
              onClick={() => {
                if (isDefined(onSave)) {
                  onSave();

                  return;
                }
                formRef.current?.requestSubmit();
              }}
              type="button">
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
